import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
  Avatar,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Alert,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const AccountSelector = ({ accounts, selectedAccounts, onAccountSelect, multiSelect = false }) => {
  // Handle account selection
  const handleAccountClick = (account) => {
    if (multiSelect) {
      // For multi-select, toggle the account
      const isSelected = selectedAccounts?.some(acc => acc.id === account.id);
      if (isSelected) {
        // Remove account
        onAccountSelect(selectedAccounts.filter(acc => acc.id !== account.id));
      } else {
        // Add account (max 2)
        if (!selectedAccounts || selectedAccounts.length < 2) {
          onAccountSelect([...(selectedAccounts || []), account]);
        }
      }
    } else {
      // For single select, just set the account
      onAccountSelect(account);
    }
  };

  // Check if account is selected
  const isAccountSelected = (account) => {
    if (multiSelect) {
      return selectedAccounts?.some(acc => acc.id === account.id) || false;
    }
    return selectedAccounts?.id === account.id;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Facebook Account{multiSelect ? 's' : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {multiSelect 
          ? 'Choose up to 2 Facebook accounts where you want to create the campaigns.'
          : 'Choose the Facebook account where you want to create the campaign.'
        }
      </Typography>

      {multiSelect && selectedAccounts?.length >= 2 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Maximum of 2 accounts can be selected
        </Alert>
      )}

      <Grid container spacing={2}>
        {accounts.map((account) => (
          <Grid item xs={12} sm={6} key={account.id}>
            <Card 
              sx={{ 
                position: 'relative',
                border: isAccountSelected(account) ? 2 : 1,
                borderColor: isAccountSelected(account) ? 'primary.main' : 'divider',
                boxShadow: isAccountSelected(account) ? 3 : 1,
                transition: 'all 0.2s ease-in-out',
                opacity: multiSelect && selectedAccounts?.length >= 2 && !isAccountSelected(account) ? 0.6 : 1,
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardActionArea
                onClick={() => handleAccountClick(account)}
                sx={{ p: 0 }}
                disabled={multiSelect && selectedAccounts?.length >= 2 && !isAccountSelected(account)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        mr: 2, 
                        width: 48, 
                        height: 48 
                      }}
                    >
                      <AccountBalanceIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" component="div">
                        {account.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {account.id}
                      </Typography>
                    </Box>
                    {isAccountSelected(account) && (
                      <CheckCircleIcon 
                        color="primary" 
                        sx={{ 
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          fontSize: 24
                        }} 
                      />
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label="Facebook Ads" 
                      size="small" 
                      color="primary" 
                      variant="outlined" 
                    />
                    <Chip 
                      label="Active" 
                      size="small" 
                      color="success" 
                      variant="outlined" 
                    />
                  </Box>
                  
                  {account.page_id && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ mt: 2, display: 'block' }}
                    >
                      Default Page ID: {account.page_id}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {((multiSelect && selectedAccounts?.length > 0) || (!multiSelect && selectedAccounts)) && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
          <Typography variant="body2" color="success.dark">
            {multiSelect ? (
              <>
                ✓ Selected account{selectedAccounts.length > 1 ? 's' : ''}: {' '}
                <strong>{selectedAccounts.map(acc => acc.name).join(', ')}</strong>
              </>
            ) : (
              <>
                ✓ Selected account: <strong>{selectedAccounts.name}</strong>
              </>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AccountSelector;